/**
 * ThemeProvider - User-selectable standardised colour themes
 * 
 * Semantic colour tokens contract:
 * - All components use semantic tokens, not raw hex
 * 
 * Three themes, each with Light/Dark mode:
 * - system: System (Material) - DEFAULT
 * - apple: Apple (High Contrast)
 * - classic: Classic (Soft)
 */
import { createContext, useContext, useEffect, useState } from "react";

// Theme definitions with CSS custom property values
const THEMES = {
  system: {
    name: 'System (Material)',
    light: {
      '--bg-app': '#f8f9fb',
      '--bg-surface': '#ffffff',
      '--bg-surface-elevated': '#ffffff',
      '--text-primary': '#0f172a',
      '--text-secondary': '#334155',
      '--text-muted': '#64748b',
      '--border-default': '#e2e8f0',
      '--border-strong': '#cbd5e1',
      '--accent': '#2563eb',
      '--accent-hover': '#1d4ed8',
      '--accent-text': '#ffffff',
      '--success': '#16a34a',
      '--warning': '#d97706',
      '--danger': '#dc2626',
      // Tailwind/Shadcn mappings (HSL format)
      '--background': '210 20% 98%',
      '--foreground': '222 47% 11%',
      '--card': '0 0% 100%',
      '--card-foreground': '222 47% 11%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '222 47% 11%',
      '--primary': '221 83% 53%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '210 20% 96%',
      '--secondary-foreground': '222 47% 11%',
      '--muted': '210 20% 96%',
      '--muted-foreground': '215 16% 40%',
      '--accent-hsl': '221 83% 53%',
      '--accent-foreground': '0 0% 100%',
      '--destructive': '0 84% 45%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '214 20% 85%',
      '--input': '214 20% 85%',
      '--ring': '221 83% 53%',
    },
    dark: {
      '--bg-app': '#0b1220',
      '--bg-surface': '#111827',
      '--bg-surface-elevated': '#1f2933',
      '--text-primary': '#f8fafc',
      '--text-secondary': '#cbd5e1',
      '--text-muted': '#94a3b8',
      '--border-default': '#1f2937',
      '--border-strong': '#334155',
      '--accent': '#3b82f6',
      '--accent-hover': '#2563eb',
      '--accent-text': '#ffffff',
      '--success': '#22c55e',
      '--warning': '#f59e0b',
      '--danger': '#ef4444',
      // Tailwind/Shadcn mappings
      '--background': '220 23% 5%',
      '--foreground': '210 40% 98%',
      '--card': '220 20% 10%',
      '--card-foreground': '210 40% 98%',
      '--popover': '220 20% 10%',
      '--popover-foreground': '210 40% 98%',
      '--primary': '217 91% 60%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '217 19% 17%',
      '--secondary-foreground': '210 40% 98%',
      '--muted': '217 19% 17%',
      '--muted-foreground': '215 16% 60%',
      '--accent-hsl': '217 91% 60%',
      '--accent-foreground': '0 0% 100%',
      '--destructive': '0 72% 51%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '217 19% 20%',
      '--input': '217 19% 20%',
      '--ring': '217 91% 60%',
    },
  },
  apple: {
    name: 'Apple (High Contrast)',
    light: {
      '--bg-app': '#f5f5f7',
      '--bg-surface': '#ffffff',
      '--bg-surface-elevated': '#ffffff',
      '--text-primary': '#1d1d1f',
      '--text-secondary': '#3a3a3c',
      '--text-muted': '#6e6e73',
      '--border-default': '#d2d2d7',
      '--border-strong': '#bdbdc2',
      '--accent': '#007aff',
      '--accent-hover': '#0063cc',
      '--accent-text': '#ffffff',
      '--success': '#28cd41',
      '--warning': '#ff9f0a',
      '--danger': '#ff3b30',
      // Tailwind/Shadcn mappings
      '--background': '240 5% 96%',
      '--foreground': '0 0% 10%',
      '--card': '0 0% 100%',
      '--card-foreground': '0 0% 10%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '0 0% 10%',
      '--primary': '211 100% 50%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '240 5% 96%',
      '--secondary-foreground': '0 0% 10%',
      '--muted': '240 5% 96%',
      '--muted-foreground': '240 2% 45%',
      '--accent-hsl': '211 100% 50%',
      '--accent-foreground': '0 0% 100%',
      '--destructive': '4 100% 59%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '240 4% 83%',
      '--input': '240 4% 83%',
      '--ring': '211 100% 50%',
    },
    dark: {
      '--bg-app': '#000000',
      '--bg-surface': '#1c1c1e',
      '--bg-surface-elevated': '#2c2c2e',
      '--text-primary': '#ffffff',
      '--text-secondary': '#ebebf5',
      '--text-muted': '#8e8e93',
      '--border-default': '#38383a',
      '--border-strong': '#48484a',
      '--accent': '#0a84ff',
      '--accent-hover': '#409cff',
      '--accent-text': '#ffffff',
      '--success': '#30d158',
      '--warning': '#ffd60a',
      '--danger': '#ff453a',
      // Tailwind/Shadcn mappings
      '--background': '0 0% 0%',
      '--foreground': '0 0% 100%',
      '--card': '0 0% 11%',
      '--card-foreground': '0 0% 100%',
      '--popover': '0 0% 11%',
      '--popover-foreground': '0 0% 100%',
      '--primary': '211 100% 52%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '0 0% 17%',
      '--secondary-foreground': '0 0% 100%',
      '--muted': '0 0% 17%',
      '--muted-foreground': '240 2% 56%',
      '--accent-hsl': '211 100% 52%',
      '--accent-foreground': '0 0% 100%',
      '--destructive': '4 100% 62%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '0 0% 23%',
      '--input': '0 0% 23%',
      '--ring': '211 100% 52%',
    },
  },
  classic: {
    name: 'Classic (Soft)',
    light: {
      '--bg-app': '#f4f6f8',
      '--bg-surface': '#ffffff',
      '--bg-surface-elevated': '#fafafa',
      '--text-primary': '#1f2937',
      '--text-secondary': '#4b5563',
      '--text-muted': '#6b7280',
      '--border-default': '#e5e7eb',
      '--border-strong': '#d1d5db',
      '--accent': '#4f46e5',
      '--accent-hover': '#4338ca',
      '--accent-text': '#ffffff',
      '--success': '#059669',
      '--warning': '#b45309',
      '--danger': '#b91c1c',
      // Tailwind/Shadcn mappings
      '--background': '216 20% 97%',
      '--foreground': '220 13% 18%',
      '--card': '0 0% 100%',
      '--card-foreground': '220 13% 18%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '220 13% 18%',
      '--primary': '243 75% 59%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '220 13% 95%',
      '--secondary-foreground': '220 13% 18%',
      '--muted': '220 13% 95%',
      '--muted-foreground': '220 9% 46%',
      '--accent-hsl': '243 75% 59%',
      '--accent-foreground': '0 0% 100%',
      '--destructive': '0 72% 41%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '220 13% 91%',
      '--input': '220 13% 91%',
      '--ring': '243 75% 59%',
    },
    dark: {
      '--bg-app': '#111827',
      '--bg-surface': '#1f2937',
      '--bg-surface-elevated': '#2a3441',
      '--text-primary': '#f3f4f6',
      '--text-secondary': '#d1d5db',
      '--text-muted': '#9ca3af',
      '--border-default': '#374151',
      '--border-strong': '#4b5563',
      '--accent': '#6366f1',
      '--accent-hover': '#4f46e5',
      '--accent-text': '#ffffff',
      '--success': '#10b981',
      '--warning': '#f59e0b',
      '--danger': '#ef4444',
      // Tailwind/Shadcn mappings
      '--background': '220 20% 10%',
      '--foreground': '220 13% 95%',
      '--card': '218 17% 17%',
      '--card-foreground': '220 13% 95%',
      '--popover': '218 17% 17%',
      '--popover-foreground': '220 13% 95%',
      '--primary': '239 84% 67%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '217 19% 27%',
      '--secondary-foreground': '220 13% 95%',
      '--muted': '217 19% 27%',
      '--muted-foreground': '218 11% 65%',
      '--accent-hsl': '239 84% 67%',
      '--accent-foreground': '0 0% 100%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '217 19% 35%',
      '--input': '217 19% 35%',
      '--ring': '239 84% 67%',
    },
  },
};

const ThemeContext = createContext({
  theme: "system",
  mode: "dark",
  effectiveMode: "dark",
  setTheme: () => null,
  setMode: () => null,
  toggleMode: () => null,
});

export function ThemeProvider({ children, storageKey = "eventsflo" }) {
  // Theme: system | apple | classic
  const [theme, setThemeState] = useState(() => {
    const stored = localStorage.getItem(`${storageKey}-theme`);
    return stored || "system";
  });

  // Mode: light | dark | system
  const [mode, setModeState] = useState(() => {
    const stored = localStorage.getItem(`${storageKey}-mode`);
    return stored || "system";
  });

  // Compute effective mode
  const getEffectiveMode = () => {
    if (mode === "system") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return mode;
  };

  const [effectiveMode, setEffectiveMode] = useState(getEffectiveMode);

  // Apply theme and mode to document
  useEffect(() => {
    const currentMode = getEffectiveMode();
    setEffectiveMode(currentMode);

    const root = document.documentElement;
    const themeTokens = THEMES[theme]?.[currentMode] || THEMES.system.dark;

    // Apply all CSS custom properties
    Object.entries(themeTokens).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // Set mode class on html and body
    root.classList.remove("light", "dark");
    root.classList.add(currentMode);
    document.body.classList.remove("light", "dark");
    document.body.classList.add(currentMode);

    // Persist preferences
    localStorage.setItem(`${storageKey}-theme`, theme);
    localStorage.setItem(`${storageKey}-mode`, mode);
  }, [theme, mode, storageKey]);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    
    const handleChange = () => {
      if (mode === "system") {
        setEffectiveMode(mediaQuery.matches ? "dark" : "light");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [mode]);

  const setTheme = (newTheme) => {
    if (THEMES[newTheme]) {
      setThemeState(newTheme);
    }
  };

  const setMode = (newMode) => {
    if (["light", "dark", "system"].includes(newMode)) {
      setModeState(newMode);
    }
  };

  const toggleMode = () => {
    setModeState(effectiveMode === "dark" ? "light" : "dark");
  };

  const value = {
    theme,
    mode,
    effectiveMode,
    setTheme,
    setMode,
    toggleMode,
    themes: THEMES,
    themeOptions: [
      { value: "system", label: "System (Material)" },
      { value: "apple", label: "Apple (High Contrast)" },
      { value: "classic", label: "Classic (Soft)" },
    ],
    modeOptions: [
      { value: "light", label: "Light" },
      { value: "dark", label: "Dark" },
      { value: "system", label: "System" },
    ],
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export { THEMES };
